import { DeeplinkKeys } from '@/consts/deepLinks';
import { getContext } from '@/context';

import { DeeplinkKind } from './types';

/**
 * Module-private URL mutator. Only the DeeplinkRouter should call this.
 * External consumers must never touch the URL — they receive a parsed
 * deeplink from the snapshot and let the router strip it after their
 * handler resolves.
 *
 * Only one-shot trigger params are stripped. Navigational params (presetId,
 * genaicategory, task_id) stay in the URL so the link remains shareable and a
 * reload restores the same view; presetJsonUrl however arms an auto-generation
 * and must not re-fire on reload.
 */
const STRIPPED_KEYS: Partial<Record<DeeplinkKind, DeeplinkKeys>> = {
  [DeeplinkKind.ProjectJson]: DeeplinkKeys.presetJsonUrl,
};

/**
 * Deeplinks are read from the host-synced SDK location model
 * (`{ params, route }`). Dispatching the reduced params tells the host to drop
 * the param from the visible URL.
 */
const stripFromSdkLocation = (key: string): void => {
  const { params, route } = getContext().general.location.getState();
  if (!params[key]) return;
  const { [key]: _removed, ...remainingParams } = params;
  getContext().general.location.dispatch({ route, params: remainingParams });
};

/**
 * Removes a resolved deeplink param from the SDK location model, mirroring the
 * read source in `extractUrlParams` so read and strip can never target
 * different places.
 */
export const stripDeeplinkUrl = (kind: DeeplinkKind): void => {
  const key = STRIPPED_KEYS[kind];
  if (!key) return;

  try {
    stripFromSdkLocation(key);
  } catch {
    // URL mutation is best-effort — never break the flow over a stale param.
  }
};
